(function () {
  if (window.customElements.get('dso-delivery-message')) {
    return;
  }

  const messageEndpoint = '/apps/dso-delivery/message';
  const cartEndpoint = '/apps/dso-delivery/cart';
  const productEndpoint = '/apps/dso-delivery/product';
  const alternativesEndpoint = '/apps/dso-delivery/alternatives';
  const inflightCartRequests = new Map();
  const productDeliveryCache = new Map();
  const alternativesCache = new Map();
  const productJsonCache = new Map();
  const alternativesTriggerStates = ['discontinued', 'pre_order'];

  const originalFetch = window.fetch;
  const cartMutationPath = /^\/cart\/(add|change|update|clear)(\.js)?$/;
  function isCartMutationRequest(input) {
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      if (!url) {
        return false;
      }
      const pathname = new URL(url, window.location.origin).pathname;
      return cartMutationPath.test(pathname);
    } catch (error) {
      return false;
    }
  }

  function fetchCart() {
    return originalFetch
      .call(window, '/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json());
  }

  async function fetchJson(url, options, fallbackMessage) {
    const response = await fetch(url, options);
    const payload = await response.json();
    if (!response.ok || payload.userErrors?.length) {
      const error = new Error(payload.userErrors?.[0]?.message || fallbackMessage);
      error.userErrors = payload.userErrors;
      throw error;
    }
    return payload;
  }

  function fetchOnce(pendingRequests, key, startFetch) {
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key);
    }
    const promise = startFetch().finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, promise);
    return promise;
  }

  function fetchProductDelivery(productId) {
    if (productDeliveryCache.has(productId)) {
      return productDeliveryCache.get(productId);
    }

    const params = new URLSearchParams({ product_id: productId });
    const promise = fetchJson(
      `${productEndpoint}?${params}`,
      { headers: { Accept: 'application/json' } },
      'Delivery message unavailable'
    )
      .then((payload) => new Map((payload.data?.variants || []).map((variant) => [variant.variantId, variant])))
      .catch((error) => {
        error.noSupplier = error.userErrors?.some((err) => err.field === 'product_id') || false;
        if (!error.noSupplier) {
          productDeliveryCache.delete(productId);
        }
        throw error;
      });

    productDeliveryCache.set(productId, promise);
    return promise;
  }

  function alternativesCacheKey(productId, size) {
    return `${productId}:${size || ''}`;
  }

  function fetchAlternatives(productId, size) {
    const key = alternativesCacheKey(productId, size);
    if (alternativesCache.has(key)) {
      return alternativesCache.get(key);
    }

    const params = new URLSearchParams({ product_id: productId });
    if (size) {
      params.set('size', size);
    }

    const promise = fetchJson(
      `${alternativesEndpoint}?${params}`,
      { headers: { Accept: 'application/json' } },
      'Alternatives unavailable'
    )
      .then((payload) => payload.data)
      .catch((error) => {
        alternativesCache.delete(key);
        throw error;
      });

    alternativesCache.set(key, promise);
    return promise;
  }

  const resolvedAlternativesCache = new Map();

  function resolveAlternativeCandidates(productId, size, limit) {
    const key = `${alternativesCacheKey(productId, size)}:${limit}`;
    if (resolvedAlternativesCache.has(key)) {
      return resolvedAlternativesCache.get(key);
    }

    const candidateBuffer = 2;
    const promise = fetchAlternatives(productId, size)
      .then((data) => {
        const candidates = ((data && data.alternatives) || [])
          .filter((alt) => alt.inStock && alt.handle)
          .sort((a, b) => a.position - b.position)
          .slice(0, limit + candidateBuffer);

        return Promise.all(
          candidates.map(async (alt) => {
            try {
              const product = await fetchProductJson(alt.handle);
              const variant = findAvailableVariantBySize(product, size);
              if (!variant) {
                console.warn(`[dso-alternatives] Skipping "${alt.handle}": no available variant matches size "${size}"`);
                return null;
              }
              return { alt, variant };
            } catch (error) {
              console.warn(`[dso-alternatives] Skipping "${alt.handle}": could not load product`, error);
              return null;
            }
          })
        ).then((results) => results.filter(Boolean).slice(0, limit));
      })
      .catch((error) => {
        resolvedAlternativesCache.delete(key);
        throw error;
      });

    resolvedAlternativesCache.set(key, promise);
    return promise;
  }

  function fetchProductJson(handle) {
    if (productJsonCache.has(handle)) {
      return productJsonCache.get(handle);
    }

    const promise = fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Product unavailable');
        }
        return response.json();
      })
      .catch((error) => {
        productJsonCache.delete(handle);
        throw error;
      });

    productJsonCache.set(handle, promise);
    return promise;
  }

  function findAvailableVariantBySize(product, size) {
    const variants = (product && product.variants) || [];
    if (!size) {
      return variants.find((variant) => variant.available) || null;
    }

    return variants.find((variant) =>
      variant.available && (variant.options || []).some((option) => typeof option === 'string' && option.includes(size))
    ) || null;
  }

  function extractSizeLabel(options) {
    return (options || []).find((option) => typeof option === 'string' && option.includes('mm x')) || '';
  }

  function sizeFromOptions(options) {
    const sizeOption = extractSizeLabel(options);
    if (!sizeOption) {
      return '';
    }
    return sizeOption.split('x').pop().trim().split('mm')[0].trim();
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') {
      return '';
    }

    const format = window.theme.moneyFormat;
    const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || '';
    const decimals = placeholder.includes('no_decimals') ? 0 : 2;
    const usesCommaSeparator = placeholder.includes('comma_separator');
    const usesSpaceSeparator = placeholder.includes('space_separator');
    const thousands = usesCommaSeparator ? '.' : usesSpaceSeparator ? ' ' : ',';
    const decimalMark = usesCommaSeparator || usesSpaceSeparator ? ',' : '.';

    const [dollars, remainder] = (cents / 100).toFixed(decimals).split('.');
    const groupedDollars = dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const value = remainder ? `${groupedDollars}${decimalMark}${remainder}` : groupedDollars;

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function itemsKey(items) {
    return items
      .map((item) => `${item.key}:${item.product_id}:${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  function fetchCartDelivery(items) {
    const key = itemsKey(items);

    return fetchOnce(inflightCartRequests, key, () =>
      fetchJson(
        cartEndpoint,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        },
        'Cart delivery messages unavailable'
      )
    );
  }

  function dispatchDeliveryState(element, detail) {
    element.dispatchEvent(new CustomEvent('dso:delivery-state', {
      bubbles: true,
      detail
    }));
  }

  function clearMessage(container) {
    container.hidden = true;
    container.textContent = '';
    container.removeAttribute('data-tone');
    container.removeAttribute('data-state');
  }

  const deliveryPropertyKey = '_dso_delivery_key';

  function displayMessage(message, state) {
    if (!message || state !== 'out_of_stock') {
      return message;
    }
    return { ...message, title: 'Made to order' };
  }

  function resolveDeliveryProperty(message) {
    if (!message || !message.body) {
      return null;
    }
    return { key: message.title || 'Delivery', value: message.body };
  }

  function resizeShopifyImage(url, width) {
    try {
      const resized = new URL(url);
      resized.searchParams.set('width', width);
      return resized.toString();
    } catch (error) {
      return url;
    }
  }

  const stateLabelAttr = {
    pre_order: 'labelPreOrder',
    discontinued: 'labelUnavailable'
  };

  function findBuyButtons(container) {
    const form = container.closest('form');
    return {
      button: form?.querySelector('.product-form__add-button'),
      wrapper: form?.querySelector('.custom-payment-buttons')
    };
  }

  function setBuyButtonsBusy({ button, wrapper }, busy) {
    wrapper?.classList.toggle('dso-buy-buttons--busy', busy);
    if (!button) {
      return;
    }

    if (busy) {
      if (button.dataset.dsoInitialDisabled === undefined) {
        button.dataset.dsoInitialDisabled = String(button.disabled);
      }
      button.disabled = true;
      button.classList.add('button--disabled');
    } else {
      const fallbackDisabled = button.dataset.dsoInitialDisabled === 'true';
      button.disabled = fallbackDisabled;
      button.classList.toggle('button--disabled', fallbackDisabled);
    }
  }

  function applyButtonState({ button, wrapper }, state) {
    wrapper?.classList.remove('dso-buy-buttons--busy');
    if (!state) {
      return;
    }

    wrapper?.classList.toggle('dso-hide-payment-button', state === 'discontinued');

    if (!button) {
      return;
    }

    const label = button.dataset[stateLabelAttr[state] || 'labelAddToCart'];

    button.disabled = state === 'discontinued';
    button.classList.toggle('button--disabled', state === 'discontinued');
    if (label) {
      button.textContent = label;
    }
  }

  function renderMessage(container, message, state) {
    if (!message || !message.body) {
      clearMessage(container);
      return;
    }

    container.hidden = false;
    container.dataset.tone = message.tone || 'neutral';
    if (state) {
      container.dataset.state = state;
    } else {
      container.removeAttribute('data-state');
    }
    container.textContent = '';

    if (message.title) {
      const title = document.createElement('p');
      title.className = 'dso-delivery-title';
      title.textContent = message.title;
      container.append(title);
    }

    const body = document.createElement('p');
    body.className = 'dso-delivery-body';
    body.textContent = message.body;
    container.append(body);
  }

  class DsoDeliveryMessage extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();

      this.buyButtons = findBuyButtons(this);

      const form = this.closest('form');
      if (form) {
        const signal = this.controller.signal;
        form.addEventListener('change', (event) => {
          if (event.target.matches('[name="quantity"]')) {
            this.refresh();
          }
        }, { signal });
      }

      if (this.dsoSkipNextRefresh) {
        this.dsoSkipNextRefresh = false;
        return;
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.fetchController?.abort();
    }

    quantity() {
      const input = this.closest('form')?.querySelector('[name="quantity"]');
      const value = parseInt(input?.value, 10);
      return value > 0 ? value : 1;
    }

    syncPropertyInput(message) {
      const form = this.closest('form');
      const valueInput = form?.querySelector('[data-dso-delivery-property]');
      const keyInput = form?.querySelector('[data-dso-delivery-key-property]');
      if (!valueInput || !keyInput) {
        return;
      }

      const resolved = resolveDeliveryProperty(message);
      if (resolved) {
        valueInput.name = `properties[${resolved.key}]`;
        valueInput.value = resolved.value;
        valueInput.disabled = false;
        keyInput.value = resolved.key;
        keyInput.disabled = false;
      } else {
        valueInput.name = '';
        valueInput.value = '';
        valueInput.disabled = true;
        keyInput.value = '';
        keyInput.disabled = true;
      }
    }

    relocate(discontinued) {
      const form = this.closest('form');
      const alternatives = form?.querySelector('dso-alternatives');
      const paymentFlex = form?.querySelector('.paymeny-flex');

      const target = discontinued && alternatives ? alternatives : paymentFlex;
      if (!target || this.nextElementSibling === target) {
        return;
      }

      this.dsoSkipNextRefresh = true;
      target.before(this);
    }

    async refresh() {
      if (this.fetchController) {
        this.fetchController.abort();
      }

      const productId = this.getAttribute('data-product-id');
      const variantId = this.getAttribute('data-variant-id');

      if (!productId || !variantId) {
        this.removeAttribute('data-dso-loading');
        clearMessage(this);
        this.syncPropertyInput(null);
        return;
      }

      const controller = new AbortController();
      this.fetchController = controller;

      await Promise.resolve();
      if (!this.isConnected || controller.signal.aborted) {
        return;
      }

      const quantity = this.quantity();

      try {
        let delivery = null;

        if (quantity === 1) {
          try {
            const byVariant = await fetchProductDelivery(productId);
            delivery = byVariant.get(variantId) || null;
          } catch (error) {
            if (error.noSupplier) {
              throw error;
            }
          }
        }

        if (controller.signal.aborted) {
          return;
        }

        if (!delivery) {
          const params = new URLSearchParams({
            product_id: productId,
            variant_id: variantId,
            quantity: String(quantity)
          });

          const payload = await fetchJson(
            `${messageEndpoint}?${params}`,
            { headers: { Accept: 'application/json' }, signal: controller.signal },
            'Delivery message unavailable'
          );

          delivery = payload.data;
        }

        const message = displayMessage(delivery && delivery.message, delivery && delivery.state);
        renderMessage(this, message, delivery && delivery.state);
        this.syncPropertyInput(message);
        applyButtonState(this.buyButtons, delivery && delivery.state);
        this.relocate(delivery && delivery.state === 'discontinued');
        dispatchDeliveryState(this, {
          productId,
          variantId,
          state: (delivery && delivery.state) || null,
          tone: (message && message.tone) || null
        });
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        clearMessage(this);
        this.syncPropertyInput(null);
        this.relocate(false);
        dispatchDeliveryState(this, { productId, variantId, state: null });
      } finally {
        if (this.fetchController === controller) {
          this.removeAttribute('data-dso-loading');
        }
      }
    }
  }

  class DsoCartDelivery extends HTMLElement {
    connectedCallback() {
      this.refresh();
    }

    lines() {
      const root = this.closest('[data-dso-cart-root]') || document;
      return Array.from(root.querySelectorAll('[data-dso-cart-line-delivery]'));
    }

    hideAll(lines) {
      clearMessage(this);
      (lines || this.lines()).forEach(clearMessage);
    }

    renderMixedMessage(mixedCartMessage) {
      if (!mixedCartMessage || !mixedCartMessage.show) {
        clearMessage(this);
        return;
      }
      renderMessage(this, { body: mixedCartMessage.body, tone: 'neutral' });
    }

    async refresh() {
      const lines = this.lines();

      const items = lines
        .map((line) => ({
          key: line.getAttribute('data-dso-cart-line-delivery'),
          product_id: Number(line.getAttribute('data-product-id')),
          variant_id: Number(line.getAttribute('data-variant-id')),
          quantity: parseInt(line.getAttribute('data-quantity'), 10) || 1
        }))
        .filter((item) => item.key && item.product_id && item.variant_id);

      if (!items.length) {
        this.hideAll(lines);
        return;
      }

      try {
        const payload = await fetchCartDelivery(items);
        const data = payload.data || {};
        const linesByKey = new Map((data.lines || []).map((line) => [line.key, line]));
        lines.forEach((container) => {
          const key = container.getAttribute('data-dso-cart-line-delivery');
          const match = linesByKey.get(key);
          renderMessage(container, match && displayMessage(match.message, match.state), match && match.state);
        });

        this.renderMixedMessage(data.mixedCartMessage);
      } catch (error) {
        this.hideAll(lines);
      }
    }
  }

  class DsoAlternatives extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.list = this.querySelector('[data-dso-alternatives-list]');
      this.heading = this.querySelector('[data-dso-alternatives-heading]');
      document.addEventListener('dso:delivery-state', (event) => this.handleDeliveryState(event), {
        signal: this.controller.signal
      });

      this.prefetch();
    }

    limit() {
      return parseInt(this.getAttribute('data-limit'), 10) || 4;
    }

    async prefetch() {
      const productId = this.getAttribute('data-product-id');
      if (!productId) {
        return;
      }

      const limit = this.limit();

      try {
        const byVariant = await fetchProductDelivery(productId);
        const triggerVariants = Array.from(byVariant.values()).filter((variant) =>
          alternativesTriggerStates.includes(variant.state)
        );

        if (!triggerVariants.length) {
          return;
        }

        const handle = this.getAttribute('data-handle');
        if (!handle) {
          return;
        }

        const product = await fetchProductJson(handle);
        const productVariantsById = new Map((product.variants || []).map((variant) => [String(variant.id), variant]));

        const sizes = new Set(
          triggerVariants.map((variant) => {
            const productVariant = productVariantsById.get(String(variant.variantId));
            return productVariant ? sizeFromOptions(productVariant.options) : '';
          })
        );

        sizes.forEach((size) => {
          resolveAlternativeCandidates(productId, size, limit).catch(() => {});
        });
      } catch (error) {
        console.warn('[dso-alternatives] Could not prefetch alternatives for product', productId, error);
      }
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    hide() {
      this.renderGeneration = (this.renderGeneration || 0) + 1;
      this.hidden = true;
      delete this.dataset.tone;
      delete this.dataset.state;
      if (this.heading) {
        this.heading.hidden = true;
      }
      if (this.list) {
        this.list.textContent = '';
      }
      this.setJoined(false);
    }

    setJoined(joined) {
      this.classList.toggle('dso-alternatives--joined', joined);
      const prev = this.previousElementSibling;
      if (prev && prev.tagName === 'DSO-DELIVERY-MESSAGE') {
        prev.classList.toggle('dso-delivery-message--joined', joined);
      }
    }

    handleDeliveryState(event) {
      const productId = this.getAttribute('data-product-id');
      if (!productId || String(event.detail.productId) !== String(productId)) {
        return;
      }

      if (!alternativesTriggerStates.includes(event.detail.state)) {
        this.hide();
        return;
      }

      if (event.detail.tone) {
        this.dataset.tone = event.detail.tone;
      } else {
        delete this.dataset.tone;
      }
      this.dataset.state = event.detail.state;

      this.load(productId);
    }

    async load(productId) {
      const size = this.getAttribute('data-size');
      const generation = (this.renderGeneration = (this.renderGeneration || 0) + 1);

      try {
        const resolved = await resolveAlternativeCandidates(productId, size, this.limit());
        if (this.renderGeneration !== generation) {
          return;
        }
        this.render(resolved);
      } catch (error) {
        if (this.renderGeneration !== generation) {
          return;
        }
        this.hide();
      }
    }

    render(resolved) {
      if (!this.list || !resolved || resolved.length === 0) {
        this.hide();
        return;
      }

      this.list.textContent = '';

      for (const { alt, variant } of resolved) {
        const link = document.createElement('a');
        link.className = 'dso-alternatives__item';
        link.href = variant.id ? `/products/${alt.handle}?variant=${variant.id}` : `/products/${alt.handle}`;

        if (alt.image) {
          const imageWrapper = document.createElement('span');
          imageWrapper.className = 'dso-alternatives__image-wrapper';

          const image = document.createElement('img');
          image.className = 'dso-alternatives__image';
          image.src = resizeShopifyImage(alt.image, 200);
          image.loading = 'lazy';
          image.alt = alt.title || '';

          imageWrapper.append(image);
          link.append(imageWrapper);
        }

        const info = document.createElement('span');
        info.className = 'dso-alternatives__info';

        const title = document.createElement('span');
        title.className = 'dso-alternatives__title';
        title.textContent = alt.title;
        info.append(title);

        if (typeof variant.price === 'number') {
          const price = document.createElement('span');
          price.className = 'dso-alternatives__price';
          price.textContent = formatMoney(variant.price);
          info.append(price);
        }

        const label = extractSizeLabel(variant.options);
        if (label) {
          const sizeLabel = document.createElement('span');
          sizeLabel.className = 'dso-alternatives__size';
          sizeLabel.textContent = label;
          info.append(sizeLabel);
        }

        link.append(info);
        this.list.append(link);
      }

      if (this.heading) {
        this.heading.hidden = false;
      }

      this.hidden = false;
      this.setJoined(this.dataset.state === 'discontinued');
    }
  }

  /** upcart drawer **/
  function upcartItemsFromCart(cart) {
    return ((cart && cart.items) || [])
      .map((item) => ({
        key: item.key,
        product_id: Number(item.product_id !== undefined ? item.product_id : item.productId),
        variant_id: Number(item.variant_id !== undefined ? item.variant_id : item.variantId),
        quantity: Number(item.quantity) || 1
      }))
      .filter((item) => item.key && item.product_id && item.variant_id);
  }

  function ensureMessageElement(scope, anchor, className) {
    if (!anchor || !anchor.parentElement) {
      return null;
    }

    const existing = scope.querySelector('.' + className);
    if (existing && existing.parentElement === anchor.parentElement) {
      return existing;
    }

    const el = document.createElement('div');
    el.className = className;
    el.hidden = true;
    anchor.parentElement.insertBefore(el, anchor.nextSibling);
    return el;
  }

  function ensureUpcartContainer(drawer) {
    return ensureMessageElement(drawer, drawer.querySelector('.upcart-public-cart-summary'), 'dso-upcart-delivery-message');
  }

  async function refreshUpcartDelivery(cartData) {
    if (!cartData && typeof window.upcartGetCart !== 'function') {
      return;
    }

    const drawer = document.getElementById('UpcartPopup');
    if (!drawer) {
      return;
    }

    const container = ensureUpcartContainer(drawer);

    function clearEverything() {
      if (container) {
        clearMessage(container);
      }
    }

    let cart = cartData;
    if (!cart) {
      try {
        cart = await window.upcartGetCart();
      } catch (error) {
        clearEverything();
        return;
      }
    }

    const items = upcartItemsFromCart(cart);

    if (!items.length) {
      clearEverything();
      return;
    }

    try {
      const payload = await fetchCartDelivery(items);
      const data = payload.data || {};

      if (container) {
        const mixedCartMessage = data.mixedCartMessage;
        renderMessage(container, mixedCartMessage && mixedCartMessage.show ? { body: mixedCartMessage.body, tone: 'neutral' } : null);
      }
    } catch (error) {
      clearEverything();
    }
  }

  async function applyDeliveryPropertyToCartLine(cartLine, lineIndex, message) {
    const resolved = resolveDeliveryProperty(message);

    const previousKey = cartLine.properties?.[deliveryPropertyKey] || '';
    const previousValue = (previousKey && cartLine.properties?.[previousKey]) || '';
    const nextKey = resolved?.key || '';
    const nextValue = resolved?.value || '';

    if (nextKey === previousKey && nextValue === previousValue) {
      return;
    }

    const properties = { ...cartLine.properties };
    if (previousKey && previousKey !== nextKey) {
      delete properties[previousKey];
    }

    if (nextKey) {
      properties[nextKey] = nextValue;
      properties[deliveryPropertyKey] = nextKey;
    } else {
      delete properties[deliveryPropertyKey];
    }

    await originalFetch.call(window, '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: cartLine.quantity, properties })
    });
  }

  let propertySyncInFlight = false;
  let propertySyncPending = false;

  async function runPropertySyncPass(cart) {
    const items = upcartItemsFromCart(cart);
    const payload = await fetchCartDelivery(items);
    const data = payload.data || {};
    const linesByKey = new Map((data.lines || []).map((line) => [line.key, line]));

    for (let i = 0; i < cart.items.length; i++) {
      const cartLine = cart.items[i];
      const match = linesByKey.get(cartLine.key);
      await applyDeliveryPropertyToCartLine(cartLine, i + 1, match && displayMessage(match.message, match.state));
    }
  }

  async function syncCartLineProperties(cart) {
    if (!cart || !cart.items || !cart.items.length) {
      return;
    }

    if (propertySyncInFlight) {
      propertySyncPending = true;
      return;
    }

    propertySyncInFlight = true;
    let nextCart = cart;

    try {
      while (nextCart) {
        const current = nextCart;
        nextCart = null;
        propertySyncPending = false;

        try {
          await runPropertySyncPass(current);
        } catch (error) {
          console.warn('[dso-delivery] Cart line property sync pass failed', error);
        }

        if (propertySyncPending) {
          try {
            nextCart = await fetchCart();
          } catch (error) {
            console.warn('[dso-delivery] Could not re-fetch cart for queued property sync', error);
            nextCart = null;
          }
        }
      }
    } finally {
      propertySyncInFlight = false;
    }
  }

  function refreshDeliveryState(cartPromise) {
    return cartPromise
      .then((cart) => {
        refreshUpcartDelivery(cart);
        syncCartLineProperties(cart);
      })
      .catch((error) => {
        console.warn('[dso-delivery] Could not fetch cart for delivery state refresh', error);
        refreshUpcartDelivery();
      });
  }

  if (typeof window.fetch === 'function' && !window.__dsoCartFetchPatched) {
    window.__dsoCartFetchPatched = true;

    window.fetch = function (input, init) {
      const result = originalFetch.call(this, input, init);

      try {
        if (isCartMutationRequest(input)) {
          result.then(() => refreshDeliveryState(fetchCart()));
        }
      } catch (error) {
        console.warn('[dso-delivery] Upcart cart-mutation detection failed', error);
      }

      return result;
    };
  }

  refreshDeliveryState(fetchCart());

  window.customElements.define('dso-delivery-message', DsoDeliveryMessage);
  window.customElements.define('dso-cart-delivery', DsoCartDelivery);
  window.customElements.define('dso-alternatives', DsoAlternatives);
})();
